import React, { useRef } from 'react';
import { Box, Stack, Typography, TextField, IconButton, Tooltip, useTheme, alpha } from '@mui/material';
import { Bold, Italic, Strikethrough } from 'lucide-react';

// WhatsApp-style inline formatting → React nodes, so a preview matches how the
// message renders on a phone:  *bold*  _italic_  ~strike~  ```mono```.
// Shared so auto-replies render identically to the template builder.
// Includes https?:// links so the preview linkifies URLs the same way the inbox
// bubble does (renderRichText in WhatsAppInbox).
export const RICH_RE = /(```[\s\S]+?```|\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~|https?:\/\/[^\s]+)/g;
export const renderRichText = (text) =>
  String(text).split(RICH_RE).filter((p) => p !== undefined && p !== '').map((p, i) => {
    if (/^https?:\/\//i.test(p)) {
      return <a key={i} href={p} target="_blank" rel="noopener noreferrer" style={{ color: '#027eb5', wordBreak: 'break-all' }}>{p}</a>;
    }
    if (p.startsWith('```') && p.endsWith('```')) {
      return <Box key={i} component="span" sx={{ fontFamily: 'monospace', bgcolor: 'rgba(0,0,0,0.06)', px: 0.5, borderRadius: 0.5 }}>{p.slice(3, -3)}</Box>;
    }
    if (p.startsWith('*') && p.endsWith('*')) return <strong key={i}>{p.slice(1, -1)}</strong>;
    if (p.startsWith('_') && p.endsWith('_')) return <em key={i}>{p.slice(1, -1)}</em>;
    if (p.startsWith('~') && p.endsWith('~')) return <span key={i} style={{ textDecoration: 'line-through' }}>{p.slice(1, -1)}</span>;
    return p;
  });

const FMT_BUTTONS = [
  { icon: Bold, marker: '*', title: 'Bold' },
  { icon: Italic, marker: '_', title: 'Italic' },
  { icon: Strikethrough, marker: '~', title: 'Strikethrough' },
];

// A reusable multiline message editor with the same WhatsApp formatting toolbar
// as the template builder (bold / italic / strike / monospace) plus a live
// preview. Controlled via `value` + `onChange(nextValue)`.
const WaMessageEditor = ({ value = '', onChange, label, placeholder, minRows = 2, showPreview = true }) => {
  const theme = useTheme();
  const ref = useRef(null);

  // Wrap the current selection (or insert "text") in a formatting marker, then
  // restore the selection so it stays highlighted.
  const wrap = (marker) => {
    const el = ref.current;
    const v = value || '';
    const s = el?.selectionStart ?? v.length;
    const e = el?.selectionEnd ?? v.length;
    const sel = v.slice(s, e) || 'text';
    onChange(v.slice(0, s) + marker + sel + marker + v.slice(e));
    requestAnimationFrame(() => {
      if (el) { el.focus(); el.setSelectionRange(s + marker.length, s + marker.length + sel.length); }
    });
  };

  return (
    <Box>
      <Stack direction="row" spacing={0.25} alignItems="center" sx={{ mb: 0.5 }}>
        {label && <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', mr: 0.5 }}>{label}</Typography>}
        {FMT_BUTTONS.map((b) => {
          const Icon = b.icon;
          return (
            <Tooltip key={b.marker} title={b.title}>
              <IconButton size="small" onClick={() => wrap(b.marker)} sx={{ color: 'text.secondary' }}><Icon size={15} /></IconButton>
            </Tooltip>
          );
        })}
        <Tooltip title="Monospace">
          <IconButton size="small" onClick={() => wrap('```')} sx={{ color: 'text.secondary', fontSize: 12, fontWeight: 800 }}>{'</>'}</IconButton>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: 10.5, color: 'text.disabled' }}>*bold* _italic_ ~strike~</Typography>
      </Stack>

      <TextField size="small" fullWidth multiline minRows={minRows} value={value} placeholder={placeholder}
        inputRef={ref} onChange={(e) => onChange(e.target.value)}
        slotProps={{ input: { sx: { fontSize: { xs: '16px', sm: '13.5px' } } } }} />

      {showPreview && (value || '').trim() && (
        <Box sx={{ mt: 1, p: 1.25, borderRadius: 2,
          bgcolor: theme.palette.mode === 'dark' ? alpha('#0b141a', 0.5) : '#efeae2' }}>
          <Box sx={{ display: 'inline-block', maxWidth: '100%', bgcolor: theme.palette.background.paper,
            borderRadius: 2, p: 1.25, boxShadow: theme.shadows[1] }}>
            <Typography sx={{ fontSize: 13, lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {renderRichText(value)}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default WaMessageEditor;
